// SPDX-FileCopyrightText: 2026 Igor Brkic <igor@hyperglitch.com>
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Draws a display list through a camera.
 *
 * Everything is drawn in board millimetres with the canvas transform doing the
 * scaling, so a 0.25 mm track is asked for as 0.25 at every zoom. Because we own
 * both the renderer and the overlay, the diff annotations land exactly on the
 * geometry — the alignment problem §6.7 anticipated with a third-party renderer
 * does not exist here.
 */

import { contains, intersects, type BBox } from '../model/bbox.ts';
import { rotatedBoxExtent } from '../model/geometry.ts';
import type { Camera, Viewport } from './camera.ts';
import type { DisplayList, ShapeItem, TextItem } from './displaylist.ts';
import { HOLE_LAYER, layerStyle, UNSUPPORTED_LAYER } from './palette.ts';

export interface RenderOptions {
  /** Only these layers are drawn. */
  visibleLayers: ReadonlySet<string>;
  /** Multiplies every colour's alpha — the onion-skin control. */
  opacity: number;
  /**
   * When set, objects **not** in this set are drawn at `dimmedOpacity`. Used to
   * push unchanged copper into the background so the changes stand out.
   */
  emphasise?: ReadonlySet<string>;
  dimmedOpacity: number;
  /** Draw text labels. Off at low zoom, where they are unreadable anyway. */
  drawText: boolean;
  /** Uniform colour override, used to tint a whole board in onion-skin mode. */
  tint?: string;
  /**
   * Where to put this board in the panel.
   *
   * The sigmaKi addition to deltaKi's renderer. A panel is the same board drawn
   * many times at different places and angles, and one `DisplayList` can serve
   * every copy — its `Path2D`s are baked in board millimetres, so only the
   * transform differs. Without this the copies could not be drawn at all:
   * `Camera.applyTo` is a `setTransform` and wipes any outer `ctx.translate`,
   * and `Camera` has no rotation of its own.
   */
  placement?: Placement;
  /**
   * Draw only items that fit entirely inside this box, in board coordinates.
   *
   * One `.kicad_pcb` often holds many separate boards, and placing one of them
   * must not drag the whole file onto the panel. KiKit selects by the same rule
   * — `collectItems` keeps items whose bounding box fits in the source area —
   * so filtering here shows what a render would actually extract, including
   * dropping an item that straddles the boundary rather than clipping it.
   */
  clipTo?: BBox;
  /**
   * Draw the layers in this order instead of the display list's baked one.
   *
   * The list is sorted by the palette's paint order when it is built, which is
   * a sensible default and not always the one you want — checking a bottom-side
   * trace against the outline means bringing B.Cu to the front. Layers missing
   * from this list keep their usual place after the ones named here.
   */
  layerOrder?: readonly string[];
  /**
   * Alpha for filled shapes, so a layer beneath shows faintly through the one
   * above it. Strokes and drill holes are excluded: thin lines stop reading at
   * all when they fade, and a translucent hole is not a hole.
   */
  fillOpacity?: number;
}

/**
 * Where each layer's shapes sit in a display list.
 *
 * `buildDisplayList` sorts by layer, so every layer occupies one contiguous
 * run — which makes drawing them in a different order a matter of visiting the
 * runs differently rather than re-sorting tens of thousands of shapes on every
 * frame. Cached per list, since one list is shared by every placement of a
 * board.
 */
const layerSpans = new WeakMap<DisplayList, Map<string, [number, number]>>();

function spansOf(list: DisplayList): Map<string, [number, number]> {
  const cached = layerSpans.get(list);
  if (cached !== undefined) return cached;

  const spans = new Map<string, [number, number]>();
  for (let i = 0; i < list.shapes.length; i++) {
    const layer = list.shapes[i]!.layer;
    const span = spans.get(layer);
    if (span === undefined) spans.set(layer, [i, i + 1]);
    else span[1] = i + 1;
  }
  layerSpans.set(list, spans);
  return spans;
}

/** The shapes to draw, in the requested layer order. */
export function orderedShapes(list: DisplayList, order: readonly string[] | undefined): ShapeItem[] {
  if (order === undefined || order.length === 0) return list.shapes;

  const spans = spansOf(list);
  const seen = new Set<string>();
  const out: ShapeItem[] = [];

  const take = (layer: string): void => {
    if (seen.has(layer)) return;
    seen.add(layer);
    const span = spans.get(layer);
    if (span !== undefined) out.push(...list.shapes.slice(span[0], span[1]));
  };

  for (const layer of order) take(layer);
  // Later means on top, so anything the caller did not name ends up above the
  // ones it did, keeping its usual order among itself.
  for (const layer of spans.keys()) take(layer);
  return out;
}

/**
 * A board's position in the panel: the source-area `anchor` lands on `position`,
 * and the board turns about that point by `rotation` degrees counter-clockwise.
 *
 * Structurally the same as `panel/place.ts`'s `Placement`, restated here so the
 * render layer keeps depending on nothing above it.
 */
export interface Placement {
  anchor: readonly [number, number];
  position: readonly [number, number];
  rotation: number;
}

export const DEFAULT_RENDER_OPTIONS: RenderOptions = {
  visibleLayers: new Set(),
  opacity: 1,
  dimmedOpacity: 0.18,
  drawText: true,
};

/** Below this many screen pixels an item is too small to be worth drawing. */
const MIN_FEATURE_PX = 0.35;
/** Text smaller than this on screen is illegible; skip it rather than smear it. */
const MIN_TEXT_PX = 5;

export interface RenderStats {
  shapesDrawn: number;
  shapesCulled: number;
  textsDrawn: number;
  ms: number;
}

export class BoardRenderer {
  private readonly ctx: CanvasRenderingContext2D;
  private readonly dpr: number;

  constructor(ctx: CanvasRenderingContext2D, dpr = 1) {
    this.ctx = ctx;
    this.dpr = dpr;
  }

  /**
   * Draw `list` through `camera`. The caller is responsible for clearing and for
   * calling this once per board when compositing an onion-skin.
   */
  draw(list: DisplayList, camera: Camera, view: Viewport, options: RenderOptions): RenderStats {
    const started = performance.now();
    const ctx = this.ctx;
    // Item boxes are in the board's own coordinates, so the visible rectangle
    // has to come back the other way through the placement before it can cull
    // anything. Skipping this drops geometry on every rotated board — which
    // looks like a rendering bug rather than a culling one.
    const visible = unplaceBox(camera.visibleBox(view), options.placement);
    const minSize = camera.pixelSize(MIN_FEATURE_PX);
    const hairline = camera.pixelSize(1);

    let shapesDrawn = 0;
    let shapesCulled = 0;
    let textsDrawn = 0;

    ctx.save();
    camera.applyTo(ctx, this.dpr);
    applyPlacement(ctx, options.placement);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    let currentLayer = '';
    let currentColor = '';

    for (const item of orderedShapes(list, options.layerOrder)) {
      if (!options.visibleLayers.has(item.layer)) continue;
      if (options.clipTo !== undefined && !contains(options.clipTo, item.bbox)) {
        shapesCulled++;
        continue;
      }
      if (!intersects(item.bbox, visible)) {
        shapesCulled++;
        continue;
      }
      if (!isWorthDrawing(item.bbox, minSize)) {
        shapesCulled++;
        continue;
      }

      if (item.layer !== currentLayer || item.color !== undefined) {
        currentLayer = item.layer;
        currentColor = options.tint ?? item.color ?? layerStyle(item.layer).color;
      }

      const dimmed = options.emphasise !== undefined && !options.emphasise.has(item.objectId);
      // Holes are punched, not painted: they keep their own colour so a via
      // still reads as a via when the rest of the board is tinted.
      const isHole = item.layer === HOLE_LAYER;
      const color = isHole ? layerStyle(item.layer).color : currentColor;
      // A hole is a punched absence, so it stays opaque however translucent the
      // copper around it is.
      const translucent = item.filled && !isHole ? (options.fillOpacity ?? 1) : 1;
      ctx.globalAlpha = options.opacity * (dimmed ? options.dimmedOpacity : 1) * translucent;

      if (item.layer === UNSUPPORTED_LAYER) {
        this.drawUnsupported(item, hairline);
        shapesDrawn++;
        continue;
      }

      if (item.filled) {
        ctx.fillStyle = color;
        ctx.fill(item.path);
      } else {
        ctx.strokeStyle = color;
        ctx.lineWidth = item.width > 0 ? item.width : hairline;
        ctx.stroke(item.path);
      }
      shapesDrawn++;
    }

    if (options.drawText) {
      for (const item of list.texts) {
        if (!options.visibleLayers.has(item.layer)) continue;
        if (options.clipTo !== undefined && !contains(options.clipTo, item.bbox)) continue;
        if (!intersects(item.bbox, visible)) continue;
        if (item.size * camera.scale < MIN_TEXT_PX) continue;
        const dimmed = options.emphasise !== undefined && !options.emphasise.has(item.objectId);
        ctx.globalAlpha = options.opacity * (dimmed ? options.dimmedOpacity : 1);
        ctx.fillStyle = options.tint ?? layerStyle(item.layer).color;
        this.drawText(item);
        textsDrawn++;
      }
    }

    ctx.restore();
    return { shapesDrawn, shapesCulled, textsDrawn, ms: performance.now() - started };
  }

  /**
   * Magenta hatched box for content the renderer cannot draw (§6.7). The user
   * must never read blank board as "nothing there".
   */
  private drawUnsupported(item: ShapeItem, hairline: number): void {
    const ctx = this.ctx;
    const b = item.bbox;
    ctx.strokeStyle = layerStyle(UNSUPPORTED_LAYER).color;
    ctx.lineWidth = hairline;
    ctx.stroke(item.path);

    ctx.save();
    ctx.beginPath();
    ctx.rect(b.minX, b.minY, b.maxX - b.minX, b.maxY - b.minY);
    ctx.clip();
    ctx.globalAlpha *= 0.5;
    ctx.beginPath();
    const h = b.maxY - b.minY;
    const step = Math.max((b.maxX - b.minX) / 8, hairline * 6);
    for (let x = b.minX - h; x < b.maxX; x += step) {
      ctx.moveTo(x, b.minY);
      ctx.lineTo(x + h, b.maxY);
    }
    ctx.stroke();
    ctx.restore();
  }

  private drawText(item: TextItem): void {
    const ctx = this.ctx;
    ctx.save();
    ctx.translate(item.x, item.y);
    // KiCad's positive rotation is counter-clockwise on a Y-down board, which is
    // the negative direction for the canvas transform.
    if (item.angle !== 0) ctx.rotate((-item.angle * Math.PI) / 180);
    if (item.mirror) ctx.scale(-1, 1);

    ctx.font = `${item.size}px ui-monospace, monospace`;
    ctx.textAlign = item.hAlign;
    ctx.textBaseline = item.vAlign === 'middle' ? 'middle' : item.vAlign;

    const lineHeight = item.size * 1.2;
    const total = (item.lines.length - 1) * lineHeight;
    const first = item.vAlign === 'middle' ? -total / 2 : item.vAlign === 'bottom' ? -total : 0;
    for (let i = 0; i < item.lines.length; i++) {
      ctx.fillText(item.lines[i]!, 0, first + i * lineHeight);
    }
    ctx.restore();
  }
}

function isWorthDrawing(b: BBox, minSize: number): boolean {
  return b.maxX - b.minX >= minSize || b.maxY - b.minY >= minSize;
}

/**
 * Compose the placement onto the camera transform.
 *
 * Board coordinates are Y-down, so KiCad's counter-clockwise rotation is the
 * canvas's negative direction — the same reason `drawText` negates its angle.
 */
function applyPlacement(ctx: CanvasRenderingContext2D, placement: Placement | undefined): void {
  if (placement === undefined) return;
  ctx.translate(placement.position[0], placement.position[1]);
  if (placement.rotation !== 0) ctx.rotate((-placement.rotation * Math.PI) / 180);
  ctx.translate(-placement.anchor[0], -placement.anchor[1]);
}

/** Take a panel-space box back into the board's own coordinates. */
function unplaceBox(box: BBox, placement: Placement | undefined): BBox {
  if (placement === undefined) return box;
  const [px, py] = placement.position;
  const [ax, ay] = placement.anchor;
  return rotatedBoxExtent(
    { minX: box.minX - px, minY: box.minY - py, maxX: box.maxX - px, maxY: box.maxY - py },
    -placement.rotation,
    ax,
    ay,
  );
}

/** Layers a board actually draws on, in paint order, excluding empty ones. */
export function defaultVisibleLayers(list: DisplayList): Set<string> {
  return new Set(list.layers.filter((l) => layerStyle(l).visibleByDefault));
}
